import { createRoot, hydrateRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import type { SharedProps } from '@/lib/sharedProps'
import { createInertiaRouter, inertiaConfig, pageConfigs, type ComponentFile } from '@/lib/inertia'
import createEmotionCache from '@/lib/emotionCache'
import { setLocale } from '@/lib/intl'
import { setDefaultLocaleParam } from '@/lib/routes'
import { setDefaultShiftsDefinitionFromCustomer } from '@/lib/shiftTime'

const inertiaRouter = createInertiaRouter()

const pages = import.meta.glob<ComponentFile>('../../pages/**/Page.{jsx,tsx}')
const layouts = import.meta.glob<ComponentFile>('../../pages/**/Layout.{jsx,tsx}')

const configs = {
  pageConfigs: import.meta.glob<PageConfig>('../../pages/**/Page.config.{js,jsx,ts,tsx}', {
    eager: true,
    import: 'pageConfig',
  }),
  layoutConfigs: import.meta.glob<LayoutConfig>('../../pages/**/Layout.config.{js,jsx,ts,tsx}', {
    eager: true,
    import: 'layoutConfig',
  }),
}

const emotionCache = createEmotionCache()

inertiaRouter.init({ pages, layouts })
pageConfigs.init(configs)

createInertiaApp<SharedProps>({
  title: title => inertiaRouter.getTitle(title),
  resolve: component => inertiaRouter.resolve({ component }),
  setup({ el, App, props }) {
    setLocale(props.initialPage.props.locale)
    setDefaultLocaleParam(props.initialPage.props.localeParam)
    setDefaultShiftsDefinitionFromCustomer(props.initialPage.props.currentCustomer)

    if (!el) {
      return
    }

    inertiaRouter.setup(props.initialPage)

    const app = (
      <inertiaRouter.AppComponent
        App={App}
        emotionCache={emotionCache}
        props={props}
        {...inertiaRouter.appComponentProps}
      />
    )

    if (import.meta.env.VITE_RUBY_SSR_BUILD_ENABLED === 'true') {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },
  defaults: inertiaConfig,
})
